// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useMemo } from "react"
import { ChevronRight } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { cn } from "@sglara/cn"
import { format } from "date-fns"
import { enGB } from "date-fns/locale"
import type { ColumnDef } from "@tanstack/react-table"
import { useForgeEvolutionCycles } from "@/queries/queries.evolutionCycles"
import { useTableUrlState } from "@/hooks/useTableUrlState"
import { useUrlParamWriter } from "@/hooks/useUrlParamWriter"
import { FullPageSpinnerWithText } from "@/components/ui/spinner"
import SmartTable from "@/components/ui/SmartTable"
import { defineMeta } from "@/lib/column-meta"
import { CycleStatusBadge } from "@/views/evolutionCycles/cycleStatusBadge"
import { CycleIdCell } from "@/views/evolutionCycles/cycleIdCell"
import { formatCurrency } from "@/utils"

// Segmented status filter — the pill copy mirrors the design handoff
// (short labels), each mapped to its canonical `CycleStatus` (or "" for
// the unfiltered "All" state). The accessible name carries the full,
// unambiguous status word.
const STATUS_FILTERS: Array<{
  value: string
  label: string
  ariaLabel: string
}> = [
  { value: "", label: "All", ariaLabel: "All statuses" },
  { value: "running", label: "run", ariaLabel: "Running" },
  { value: "completed", label: "done", ariaLabel: "Completed" },
  { value: "failed", label: "fail", ariaLabel: "Failed" },
  { value: "expired", label: "exp", ariaLabel: "Expired" },
]

const formatCycleDate = (iso: string): string =>
  format(new Date(iso), "d MMM, HH:mm", { locale: enGB })

// The sortable column ids, fed to the shared URL-state codec so a hostile
// `?ch_sort=` decodes against the real column set (ADR-0006).
const CYCLE_HISTORY_COLUMN_IDS = [
  "cycle_id",
  "status",
  "started_at",
  "ended_at",
  "n_candidates",
  "total_cost",
] as const

const cycleHistoryColumns: ColumnDef<EvolutionCycle>[] = [
  {
    id: "cycle_id",
    header: "Cycle ID",
    accessorFn: (c) => c.cycle_id,
    cell: ({ row }) => <CycleIdCell id={row.original.cycle_id} />,
    meta: defineMeta<EvolutionCycle>({ width: 1.4 }),
  },
  {
    id: "status",
    header: "Status",
    accessorFn: (c) => c.status,
    cell: ({ row }) => <CycleStatusBadge status={row.original.status} />,
    meta: defineMeta<EvolutionCycle>({ width: 1.1 }),
  },
  {
    id: "started_at",
    header: "Started",
    accessorFn: (c) => c.started_at,
    cell: ({ row }) => (
      <span className="font-mono text-xs text-text-primary">
        {formatCycleDate(row.original.started_at)}
      </span>
    ),
    meta: defineMeta<EvolutionCycle>({ width: 1.2 }),
  },
  {
    id: "ended_at",
    header: "Finished",
    accessorFn: (c) => c.ended_at ?? null,
    cell: ({ row }) =>
      row.original.ended_at ? (
        <span className="font-mono text-xs text-text-primary">
          {formatCycleDate(row.original.ended_at)}
        </span>
      ) : (
        <span className="font-semibold text-text-muted">—</span>
      ),
    meta: defineMeta<EvolutionCycle>({ width: 1.2 }),
  },
  {
    id: "n_candidates",
    header: "Cand.",
    accessorFn: (c) => c.n_candidates,
    cell: ({ row }) => (
      <span className="font-mono text-xs text-text-primary">
        {row.original.n_candidates}
      </span>
    ),
    meta: defineMeta<EvolutionCycle>({ width: 0.6, align: "right" }),
  },
  {
    id: "total_cost",
    header: "Cost",
    // Null/absent ⇒ placeholder, never a misleading "$0.00" for a
    // running / pre-feature cycle; the null sinks under the sort.
    accessorFn: (c) => c.total_cost ?? null,
    cell: ({ row }) =>
      row.original.total_cost == null ? (
        <span className="font-semibold text-text-muted">—</span>
      ) : (
        <span className="font-mono text-xs text-text-primary">
          {formatCurrency(row.original.total_cost)}
        </span>
      ),
    meta: defineMeta<EvolutionCycle>({ width: 0.8, align: "right" }),
  },
  {
    id: "_arrow",
    header: "​",
    enableSorting: false,
    enableHiding: false,
    cell: () => (
      <ChevronRight
        aria-hidden
        className="ml-auto size-4 text-text-tertiary"
      />
    ),
    meta: defineMeta<EvolutionCycle>({ width: 0.3, align: "right" }),
  },
]

export interface ForgeCycleHistoryProps {
  forgeId: string
}

export const ForgeCycleHistory = ({ forgeId }: ForgeCycleHistoryProps) => {
  const [searchParams] = useSearchParams()
  const write = useUrlParamWriter()
  const navigate = useNavigate()

  const status = searchParams.get("status") ?? undefined
  const startedAfter = searchParams.get("started_after") ?? undefined
  const activeStatus = status ?? ""

  // Sort and current page live in the URL (ADR-0006) via the shared codec,
  // the same binding My Cycles and the Leaderboard use. Namespaced under
  // `ch_sort` / `ch_page` so they never collide with the filter params (or
  // the arena's own state) on the shared `/forges/:forgeId` route.
  const { sorting, onSortingChange, pageIndex, onPageIndexChange } =
    useTableUrlState({
      columnIds: CYCLE_HISTORY_COLUMN_IDS,
      sortParam: "ch_sort",
      pageParam: "ch_page",
    })

  const params = useMemo(
    () => ({
      // The route param accepts any string; the server ignores unknown
      // statuses (empty result rather than a 400).
      status: status as CycleStatus | undefined,
      started_after: startedAfter,
    }),
    [status, startedAfter],
  )

  const { data, isLoading, isError } = useForgeEvolutionCycles(forgeId, params)

  // A filter change resets the namespaced page — page N of the previous
  // result set is meaningless against the new one.
  const handleStatusChange = (value: string) =>
    write({ status: value, ch_page: null })

  return (
    <section
      role="region"
      aria-label="Cycle history"
      className="flex flex-col gap-3.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-tertiary">
          Cycle history · {data ? data.items.length : 0}
        </span>
        <div
          role="radiogroup"
          aria-label="Filter by status"
          className="inline-flex overflow-hidden rounded-[9px] border border-border bg-surface"
        >
          {STATUS_FILTERS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={activeStatus === opt.value}
              aria-label={opt.ariaLabel}
              onClick={() => handleStatusChange(opt.value)}
              className={cn(
                "border-r border-border px-3 py-[5px] text-[11px] font-medium transition-colors last:border-r-0",
                activeStatus === opt.value
                  ? "bg-[rgba(69,240,200,0.14)] text-mint"
                  : "text-text-secondary hover:text-foreground",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <FullPageSpinnerWithText text="Loading evolution cycles" />
      ) : isError || !data ? (
        <div
          role="alert"
          className="rounded-md border border-failed/40 bg-failed/10 p-4 text-failed-fg"
        >
          Could not load evolution cycles for {forgeId}.
        </div>
      ) : (
        <SmartTable
          data={data.items}
          columns={cycleHistoryColumns}
          pageSize={20}
          sorting={sorting}
          onSortingChange={onSortingChange}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          rowClassName={(c) =>
            c.status === "running" ? "bg-[rgba(91,141,239,0.06)]" : undefined
          }
          onRowClick={(c) => navigate(`/evolution-cycles/${c.cycle_id}`)}
        />
      )}
    </section>
  )
}
